/**
 * 返金口座情報の共通バリデーションライブラリ
 * Admin側（申込書発行時）とUser側（申込時）で共通使用
 */

/**
 * 返金口座用の正規表現パターン
 * @property {RegExp} kana - 口座名義用カナチェック（全角・半角カタカナ、スペース、長音記号、括弧、中点を許可）
 * @property {RegExp} accountNumber - 口座番号用数字チェック（半角数字のみ）
 */
const BANK_ACCOUNT_PATTERNS = {
  // 全角カタカナ、半角カタカナ、スペース、長音記号、括弧、中点
  kana: /^[ァ-ヶｦ-ﾟーｰ\s　()（）・･]+$/,
  // 半角数字のみ
  accountNumber: /^\d+$/
} as const;

/**
 * 返金口座情報の制約値
 * @property {number} maxLength - 文字列の最大長（255文字）
 * @property {number} accountNumberLength - 口座番号の固定桁数（7桁）
 */
const BANK_ACCOUNT_CONSTRAINTS = {
  maxLength: 255,
  accountNumberLength: 7
} as const;

/**
 * 返金口座情報のエラーメッセージ定義
 * 各フィールドごとに必須エラー、文字数エラー、形式エラーを定義
 */
const BANK_ACCOUNT_ERROR_MESSAGES = {
  bankName: {
    required: '金融機関名を入力してください',
    maxLength: `${BANK_ACCOUNT_CONSTRAINTS.maxLength}文字以内で入力してください`
  },
  branchName: {
    required: '支店名を入力してください',
    maxLength: `${BANK_ACCOUNT_CONSTRAINTS.maxLength}文字以内で入力してください`
  },
  accountType: {
    required: '口座種別を選択してください',
    invalid: '口座種別を正しく選択してください'
  },
  accountHolderName: {
    required: '口座名義を入力してください',
    maxLength: `${BANK_ACCOUNT_CONSTRAINTS.maxLength}文字以内で入力してください`,
    format: 'カタカナで入力してください'
  },
  accountNumber: {
    required: '口座番号を入力してください',
    format: '口座番号は半角数字で入力してください',
    length: '口座番号は7桁で入力してください'
  }
} as const;

/**
 * 銀行名のバリデーション
 * @param {string} value - 入力された金融機関名
 * @param {boolean} isRequired - 必須チェックを行うか（Admin: false, User: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateBankName = (value: string, isRequired = true): string | undefined => {
  if (!value || value.trim() === '') {
    return isRequired ? BANK_ACCOUNT_ERROR_MESSAGES.bankName.required : undefined;
  }
  if (value.length > BANK_ACCOUNT_CONSTRAINTS.maxLength) {
    return BANK_ACCOUNT_ERROR_MESSAGES.bankName.maxLength;
  }
  return undefined;
};

/**
 * 支店名のバリデーション
 * @param {string} value - 入力された支店名
 * @param {boolean} isRequired - 必須チェックを行うか（Admin: false, User: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateBranchName = (value: string, isRequired = true): string | undefined => {
  if (!value || value.trim() === '') {
    return isRequired ? BANK_ACCOUNT_ERROR_MESSAGES.branchName.required : undefined;
  }
  if (value.length > BANK_ACCOUNT_CONSTRAINTS.maxLength) {
    return BANK_ACCOUNT_ERROR_MESSAGES.branchName.maxLength;
  }
  return undefined;
};

/**
 * 口座種別のバリデーション
 * @param {string} value - 入力された口座種別（'1': 普通, '2': 当座）
 * @param {boolean} isRequired - 必須チェックを行うか（Admin: false, User: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateAccountType = (value: string, isRequired = true): string | undefined => {
  if (!value || value.trim() === '') {
    return isRequired ? BANK_ACCOUNT_ERROR_MESSAGES.accountType.required : undefined;
  }
  if (value !== '1' && value !== '2') {
    return BANK_ACCOUNT_ERROR_MESSAGES.accountType.invalid;
  }
  return undefined;
};

/**
 * 口座名義のバリデーション
 * 全角・半角カタカナ、スペース、括弧、中点を許可
 * @param {string} value - 入力された口座名義（カタカナ）
 * @param {boolean} isRequired - 必須チェックを行うか（Admin: false, User: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateAccountHolderName = (value: string, isRequired = true): string | undefined => {
  if (!value || value.trim() === '') {
    return isRequired ? BANK_ACCOUNT_ERROR_MESSAGES.accountHolderName.required : undefined;
  }
  if (value.length > BANK_ACCOUNT_CONSTRAINTS.maxLength) {
    return BANK_ACCOUNT_ERROR_MESSAGES.accountHolderName.maxLength;
  }
  if (!BANK_ACCOUNT_PATTERNS.kana.test(value)) {
    return BANK_ACCOUNT_ERROR_MESSAGES.accountHolderName.format;
  }
  return undefined;
};

/**
 * 口座番号のバリデーション
 * 半角数字7桁のみ許可
 * @param {string} value - 入力された口座番号
 * @param {boolean} isRequired - 必須チェックを行うか（Admin: false, User: true）
 * @returns {string | undefined} エラーメッセージ（エラーがない場合はundefined）
 */
export const validateAccountNumber = (value: string, isRequired = true): string | undefined => {
  if (!value || value.trim() === '') {
    return isRequired ? BANK_ACCOUNT_ERROR_MESSAGES.accountNumber.required : undefined;
  }
  // 半角数字のみチェック
  if (!BANK_ACCOUNT_PATTERNS.accountNumber.test(value)) {
    return BANK_ACCOUNT_ERROR_MESSAGES.accountNumber.format;
  }
  // 7桁チェック
  if (value.length !== BANK_ACCOUNT_CONSTRAINTS.accountNumberLength) {
    return BANK_ACCOUNT_ERROR_MESSAGES.accountNumber.length;
  }
  return undefined;
};
